import slugify from '@sindresorhus/slugify';
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Button, Dropdown, Icon, IconTooltip, Tooltip } from '~/components';
import { useWorkspace } from '~/contexts';
import { useAuth } from '~/hooks';
import { colors, weights } from '~/styles';
import { useReports } from '../ReportsContext';

const Box = styled(Link)`
  display: flex;
  gap: 0.75rem;
  background-color: #fff;
  border: solid 1px ${colors.grey10};
  border-radius: 5px;
  padding: 0.875rem;
  background-color: ${colors.white};
  transition: all 0.2s ease-in-out;
  cursor: pointer;
  align-items: center;

  &:hover {
    border-radius: 5px;
    box-shadow: 0 0.1875rem 1rem ${colors.grey10};
    z-index: 1;
    border-color: transparent;
  }
`;

const Title = styled.div`
  font-weight: ${weights.medium};
  color: ${colors.grey100};
  font-size: 1rem;
`;

const Favorite = styled(Button)`
  height: 100%;
  margin-left: auto;
  border: none;
  padding: 0;
  height: auto;
`;

const Right = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-left: auto;
  align-items: center;
  height: 100%;
`;

const Details = styled.div`
  margin-left: auto;
`;

function Description({ children }) {
  return (
    <Details>
      <Tooltip message={children}>
        <Icon icon="info-circle" color={colors.grey10} />
      </Tooltip>
    </Details>
  );
}

function NavBox({ report, area, onEdit, onSaveAs, onDelete }) {
  const auth = useAuth();
  const { workspace } = useWorkspace();
  const reports = useReports();

  const handleStarClick = async (event) => {
    event.preventDefault();
    if (report.isFavorite) {
      await reports.removeFavorite(report.id, 'member');
    } else {
      await reports.addFavorite(report.id, 'member');
    }
  };

  const handlePinClick = async (event) => {
    event.preventDefault();
    if (report.isPinned) {
      await reports.removePinned(report.id);
    } else {
      await reports.addPinned(report.id);
    }
  };

  const to = useMemo(() => {
    switch (report.scopeKey) {
      case 'workspace':
      case 'member':
        return `/app/${workspace.key}/reports/${area}/${report.slug}/${slugify(report.name)}`;

      default:
        return `/app/${workspace.key}/reports/${area}/${report.slug}`;
    }
  }, [area, report, workspace.key]);

  const isSaved = ['workspace', 'member'].includes(report.scopeKey);

  return (
    <Box to={to} data-testid="reports-navbox">
      <Title>{report.name}</Title>

      <Right>
        {area === 'library' && report.scopeKey === 'workspace' && (
          <IconTooltip
            icon="floppy-disk"
            message={`This report has been saved to the Library by ${report.modifiedBy?.name ?? 'a Workspace Admin'}.`}
            color={colors.grey40}
            delay={300}
          />
        )}

        {auth.workspace.manage && (
          <Favorite isOutline disabled={report.scopeKey === 'member'} onClick={handlePinClick}>
            <IconTooltip
              icon="thumbtack"
              type={report.isPinned ? 'fa' : 'far'}
              color={report.scopeKey === 'member' ? colors.grey25 : colors.primary}
              message={
                report.scopeKey === 'member'
                  ? 'This report must be saved to the Library before it can be pinned.'
                  : report.isPinned
                    ? 'Unpin this report from the Featured Reports section of the Library.'
                    : 'Pin this report to the Featured Reports section of the Library.'
              }
              delay={500}
            />
          </Favorite>
        )}

        <Favorite isOutline onClick={handleStarClick}>
          <IconTooltip
            icon="star"
            type={report.isFavorite ? 'fa' : 'far'}
            color={colors.warning}
            message={
              report.isFavorite ? 'Unfavorite this report in My Reports.' : 'Favorite this report in My Reports.'
            }
            delay={500}
          />
        </Favorite>

        {report.description && (
          <Tooltip message={report.description} style={{ display: 'flex', alignItems: 'center' }}>
            <Icon icon="info-circle" color={colors.grey10} />
          </Tooltip>
        )}

        {isSaved && (
          <Dropdown align="right">
            {({ setIsOpen }) => {
              const handleAction = (action) => setIsOpen(false) || action;

              return (
                <>
                  <Dropdown.Trigger style={{ display: 'flex' }}>
                    <Button
                      isAnchor
                      onClick={(e) => {
                        e.preventDefault();
                        setIsOpen(true);
                      }}>
                      <Icon icon="ellipsis-h" />
                    </Button>
                  </Dropdown.Trigger>
                  <Dropdown.Menu>
                    <Dropdown.Item onClick={() => handleAction()}>View</Dropdown.Item>
                    <Dropdown.Item
                      tooltip={!report.permissions.manage && 'Insufficient permissions to edit this report.'}
                      disabled={!report.permissions.manage}
                      onClick={(e) => handleAction(onEdit(e))}>
                      Edit
                    </Dropdown.Item>
                    <Dropdown.Item onClick={(e) => handleAction(onSaveAs(e))}>Save as</Dropdown.Item>
                    <Dropdown.DeleteItem
                      tooltip={!report.permissions.manage && 'Insufficient permissions to delete this report.'}
                      disabled={!report.permissions.manage}
                      onClick={(e) => handleAction(onDelete(e))}>
                      Delete
                    </Dropdown.DeleteItem>
                  </Dropdown.Menu>
                </>
              );
            }}
          </Dropdown>
        )}
      </Right>
    </Box>
  );
}

NavBox.Title = Title;
NavBox.Description = Description;

export default NavBox;
